'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light'

import useEmblaCarousel from 'embla-carousel-react'

import { getRecentlyViewedCategories } from '@/app/_lib/services/RecentlyViewedService'

// Categories that render CSS-based cards instead of landing images
// Maps category_id to background color class
const CSS_CARD_CATEGORIES = {
    10: 'bg-[#4B9ED1]',   // Designers (blue)
    11: 'bg-[#999999]',  // Brands (40% gray)
    398: 'bg-primary',
    555: 'bg-red',
    819: 'bg-[#00C378]'   // Outdoor (green)
}

const RecentlyViewedCategories = ({
    recentlyViewedData = null,
    title = 'Dive Back In',
    subtitle = 'Categories you\'ve recently browsed'
}) => {
    const [recentlyViewed, setRecentlyViewed] = useState(recentlyViewedData || [])
    const [isLoading, setIsLoading] = useState(!recentlyViewedData)
    const [canScrollPrev, setCanScrollPrev] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(false)

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: false,
        align: 'start',
        slidesToScroll: 2,
        containScroll: 'trimSnaps'
    })

    const updateScrollButtons = useCallback(() => {
        if (!emblaApi) return
        setCanScrollPrev(emblaApi.canScrollPrev())
        setCanScrollNext(emblaApi.canScrollNext())
    }, [emblaApi])

    useEffect(() => {
        if (!emblaApi) return
        updateScrollButtons()
        emblaApi.on('select', updateScrollButtons)
        emblaApi.on('reInit', updateScrollButtons)
        return () => {
            emblaApi.off('select', updateScrollButtons)
            emblaApi.off('reInit', updateScrollButtons)
        }
    }, [emblaApi, updateScrollButtons])

    const scrollPrev = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev()
    }, [emblaApi])

    const scrollNext = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext()
    }, [emblaApi])

    useEffect(() => {
        if (recentlyViewedData && recentlyViewedData.length > 0) {
            setRecentlyViewed(recentlyViewedData)
        } else if (!recentlyViewedData) {
            const items = getRecentlyViewedCategories()
            setRecentlyViewed(items)
            setIsLoading(false)
        }
    }, [recentlyViewedData])

    if (isLoading || !recentlyViewed || recentlyViewed.length === 0) {
        return null
    }

    return (
        <div className="site-padding mt-16 mb-16 w-full min-[1810px]:px-0 relative">
            <div className="mb-8">
                <h2 className="leading-[1] text-2xl font-extralight">{title}</h2>
                <p className="text-xs text-light-3 m-0">{subtitle}</p>
            </div>

            <div className="flex items-center -mr-3">
                {/* Previous button */}
                {(canScrollPrev || canScrollNext) && (
                    <button
                        onClick={scrollPrev}
                        disabled={!canScrollPrev}
                        aria-label="Previous categories"
                        className={`absolute left-[calc(var(--site-padding)+0.625rem)] top-1/2 z-40 shrink-0 ${canScrollPrev ? 'cursor-pointer' : 'cursor-default opacity-30'}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronLeft} className="text-dark text-sm" />
                        </div>
                    </button>
                )}

                {/* Carousel */}
                <div className="flex-1 min-w-0">
                    <div ref={emblaRef} className="overflow-hidden">
                        <div className="flex">
                            {recentlyViewed.map((category, index) => {
                                const categoryId = Number(category.category_id)
                                const imageData = category.category_landing || { file_name: category.category_photo }
                                const hasImage = imageData?.file_name
                                const cssCardColor = CSS_CARD_CATEGORIES[categoryId] || (!hasImage ? 'bg-gray-50' : null)

                                return (
                                    <Link
                                        key={`${category.category_id}-${index}`}
                                        href={category.url || '#'}
                                        className="group flex-none w-1/2 sm:w-1/3 md:w-1/4 xl:w-1/5 2xl:w-[16.666%] pr-3"
                                    >
                                        {cssCardColor ? (
                                            <>
                                                <div className="aspect-square bg-gray-3 mb-3 flex items-center justify-center">
                                                    <div className={`w-[55%] sm:w-[42%] aspect-square flex items-center justify-center ${cssCardColor}`}>
                                                        <span className="text-white font-medium text-xl sm:text-3xl capitalize">
                                                            {category.menu_name || category.display_name || category.category_name}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-left text-lg capitalize">
                                                    {category.display_name || category.category_name}
                                                </div>
                                            </>
                                        ) : category.designer?.designer_id ? (
                                            <>
                                                <div className="aspect-square relative overflow-hidden mb-3 bg-white flex items-center justify-center">
                                                    {imageData?.file_name && (
                                                        <Image
                                                            className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                                                            src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${imageData.file_name}`}
                                                            alt={imageData.alt_text || category.display_name || category.category_name || 'Category image'}
                                                            width={imageData.width || 250}
                                                            height={imageData.height || 250}
                                                            quality={90}
                                                            sizes="200px"
                                                        />
                                                    )}
                                                </div>
                                                <div className="text-left text-sm capitalize">
                                                    {category.display_name || category.category_name}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="aspect-square overlay-block flex justify-center bg-white mb-3 overflow-hidden p-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-3/4 after:border-b after:border-gray-5 md:group-hover:after:border-primary">
                                                    {imageData?.file_name && (
                                                        <Image
                                                            className="w-full h-full object-contain scale-75 sm:scale-85 lg:scale-100 lg:transition-all lg:duration-400 lg:group-hover:scale-105"
                                                            src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${imageData.file_name}`}
                                                            alt={imageData.alt_text || category.display_name || category.category_name || 'Category image'}
                                                            width={200}
                                                            height={200}
                                                            quality={90}
                                                            sizes="200px"
                                                        />
                                                    )}
                                                </div>
                                                <div className="text-left text-lg capitalize">
                                                    {category.display_name || category.category_name}
                                                </div>
                                            </>
                                        )}
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Next button */}
                {(canScrollPrev || canScrollNext) && (
                    <button
                        onClick={scrollNext}
                        disabled={!canScrollNext}
                        aria-label="Next categories"
                        className={`absolute right-[calc(var(--site-padding)+0.625rem)] top-1/2 z-40 shrink-0 ${canScrollNext ? 'cursor-pointer' : 'cursor-default opacity-30'}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronRight} className="text-dark text-sm" />
                        </div>
                    </button>
                )}
            </div>
        </div>
    )
}

export default RecentlyViewedCategories
